import { useCallback, useEffect, useRef, useState } from 'react';
import {environmentVariables} from "../utils/helpers/environmentVariables";
import {WebSocketClientAction, WebSocketConnectionState} from "../models/WebSocketClientActions";
import {RegattaRaceResultStatus, WebSocketEventType} from "../models/RaceDataReturnTypes";

export function useRaceDataWebSocket(regattaId: string, includePracticeRaces: boolean = false, allTeams: boolean = true, pastEvent: boolean = true) {
  const [raceData, setRaceData] = useState<RegattaRaceResultStatus | null>(null);
  const [connectionState, setConnectionState] = useState<WebSocketConnectionState>({
    status: 'initial',
  });

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  const baseReconnectTimeout = 100;
  const maxReconnectTimeout = 5000;

  const getBackoffDelay = useCallback(() => {
    const exponentialDelay = baseReconnectTimeout * Math.pow(2, reconnectAttemptsRef.current);
    const cappedDelay = Math.min(exponentialDelay, maxReconnectTimeout);
    const jitter = cappedDelay * 0.2 * (Math.random() - 0.5);

    return cappedDelay + jitter;
  }, []);

  const connectWebSocket = useCallback(() => {
    if (socketRef.current) return;

    const isReconnecting = reconnectAttemptsRef.current > 0;
    setConnectionState({
      status: isReconnecting ? 'reconnecting' : 'connecting',
      attempts: reconnectAttemptsRef.current,
    });

    const authToken = environmentVariables.websocketAuthToken();
    if (!authToken) {
      const errorMsg = 'Missing REACT_APP_WEBSOCKET_AUTH_TOKEN in environment';
      console.error('[RaceWS] ' + errorMsg);
      setConnectionState({ status: 'error', error: errorMsg });
      return;
    }

    if (!regattaId) {
      const errorMsg = 'Missing regattaId parameter';
      console.error('[RaceWS] ' + errorMsg);
      setConnectionState({ status: 'error', error: errorMsg });
      return;
    }

    const baseWsUrl = environmentVariables.websocketRoot() + 'websocket/liveevent';
    const wsUrl = `${baseWsUrl}?token=${authToken}&regattaId=${regattaId}&includePracticeRaces=${includePracticeRaces}&pastEvent=${pastEvent}&allTeams=${allTeams}`;

    try {
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        console.log('[RaceWS] Connected to regatta:', regattaId);
        reconnectAttemptsRef.current = 0;
        setConnectionState({ status: 'connected' });
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);

          if (data.type === 'heartbeat') {
          } else if (data.type === 'error') {
            console.warn('[RaceWS] Server error:', data.message || data.error);
            setConnectionState({
              status: 'error',
              error: `Server error: ${data.message || data.error || 'Unknown error'}`,
            });
          } else if (data.type === 'info') {
            console.info('[RaceWS] Server info:', data.message);
          } else if (data.event === WebSocketEventType.RACE_DATA && data.content) {
            console.log('[RaceWS] Race data update received');
            setRaceData(data.content);
          } else if (
            data.regattaId &&
            data.message &&
            data.message.includes('connected to regatta')
          ) {
            console.log('[RaceWS] Connection confirmed:', data.message);
          }
        } catch (err) {
          console.warn('[RaceWS] Failed to parse message:', err, 'Raw data:', evt.data);
        }
      };

      ws.onerror = () => {
        console.warn('[RaceWS] WebSocket error occurred');
        setConnectionState({
          status: 'error',
          error: 'WebSocket error occurred',
          attempts: reconnectAttemptsRef.current,
        });
      };

      ws.onclose = (evt) => {
        console.log('[RaceWS] Connection closed:', evt.code, evt.reason);
        socketRef.current = null;

        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setConnectionState({
            status: 'error',
            error: 'Maximum reconnection attempts reached',
            attempts: reconnectAttemptsRef.current,
          });
          return;
        }

        reconnectAttemptsRef.current++;

        const reconnectDelay = getBackoffDelay();

        if (!reconnectTimeoutRef.current) {
          console.log(
            `[RaceWS] Reconnecting in ${Math.round(reconnectDelay)}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`
          );

          setConnectionState({
            status: 'reconnecting',
            attempts: reconnectAttemptsRef.current,
          });

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, reconnectDelay);
        }
      };
    } catch (error) {
      console.error('[RaceWS] Error creating WebSocket:', error);
      setConnectionState({
        status: 'error',
        error: `Error creating WebSocket: ${error instanceof Error ? error.message : String(error)}`,
        attempts: reconnectAttemptsRef.current,
      });
    }
  }, [regattaId, includePracticeRaces, pastEvent, allTeams, getBackoffDelay]);

  const requestUpdate = useCallback(() => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          action: WebSocketClientAction.REQUEST_UPDATE,
        })
      );
    } else {
      console.warn('[RaceWS] Cannot request update: socket not open');
    }
  }, []);

  useEffect(() => {
    connectWebSocket();

    return () => {
      console.log('[RaceWS] Cleaning up connection for regatta:', regattaId);

      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [regattaId, includePracticeRaces, pastEvent, allTeams, connectWebSocket]);

  return {
    raceData,
    connectionState,
    requestUpdate,
  };
}
